// ---------------------------------------------------------------------------
// SpeedyDock SDK — Boat Preview Card Widget
// ---------------------------------------------------------------------------

import { SDK_PREFIX } from "./styles";
import type { BoatCardOptions } from "./types";
import { resolveElement, createElement } from "./utils";

/**
 * Renders a self-contained boat preview card with image, name, category,
 * capacity, pricing, and a "Book Now" CTA.
 */
export function createBoatCard(
  openBooking: (boatSlug: string, date?: string) => void,
  target: string | HTMLElement,
  options: BoatCardOptions,
): { destroy(): void } {
  const container = resolveElement(target);
  if (!container) throw new Error(`[SpeedyDock] Target "${target}" not found.`);

  const card = createElement("div", `${SDK_PREFIX}-boat-card`);

  // Image
  if (options.image) {
    const img = createElement("img", `${SDK_PREFIX}-boat-card-img`, {
      src: options.image,
      alt: options.name ?? options.boatSlug,
      loading: "lazy",
    });
    card.appendChild(img);
  } else {
    const placeholder = createElement("div", `${SDK_PREFIX}-boat-card-img`);
    placeholder.style.display = "flex";
    placeholder.style.alignItems = "center";
    placeholder.style.justifyContent = "center";
    placeholder.style.fontSize = "32px";
    placeholder.textContent = "⛵";
    card.appendChild(placeholder);
  }

  const body = createElement("div", `${SDK_PREFIX}-boat-card-body`);

  const name = createElement("p", `${SDK_PREFIX}-boat-card-name`);
  name.textContent = options.name ?? options.boatSlug;
  body.appendChild(name);

  const meta: string[] = [];
  if (options.category) meta.push(options.category);
  if (options.capacity) meta.push(`Up to ${options.capacity}`);
  if (meta.length) {
    const metaEl = createElement("p", `${SDK_PREFIX}-boat-card-meta`);
    metaEl.textContent = meta.join(" · ");
    body.appendChild(metaEl);
  }

  if (options.price) {
    const price = createElement("p", `${SDK_PREFIX}-boat-card-price`);
    price.textContent = options.price;
    body.appendChild(price);
  }

  const btn = createElement("button", `${SDK_PREFIX}-boat-card-btn`);
  btn.textContent = "Book Now";
  body.appendChild(btn);

  card.appendChild(body);
  container.appendChild(card);

  const clickHandler = () => openBooking(options.boatSlug, options.date);
  card.addEventListener("click", clickHandler);

  return {
    destroy() {
      card.removeEventListener("click", clickHandler);
      card.remove();
    },
  };
}
